import gql from 'graphql-tag';

/**
 * Queries against the aspera API -- see content-acq-aspera/cdk/content_aspera/api.graphql.
 *
 * No `export` mutation is called from the UI yet. `login` / `submit_mfa` set the endpoint's Shares login, as signiant's
 * portal login does.
 *
 * `browse` logs in to the Shares server and walks every page of one folder, so it is fetched one folder at a time
 * and only by the detail page, never in a list.
 */

export const GET_ASPERA_ENDPOINTS = gql`
  query GetAsperaEndpoints {
    endpoints {
      id
      name
      server
      sharesUrl
      shareId
      browsable
      browseAsync
      fixedEgressIp
      upload { source remoteRoot }
    }
  }
`;

/**
 * The endpoint and its processes are separate queries on purpose: a GraphQL error in one field fails the whole
 * operation, and the process table (a DynamoDB index) must not take the endpoint facts and the browser down with it.
 */
export const GET_ASPERA_ENDPOINT = gql`
  query GetAsperaEndpoint($endpointId: ID!) {
    endpoint(input: { endpointId: $endpointId }) {
      id
      name
      server
      sharesUrl
      shareId
      browsable
      browseAsync
      fixedEgressIp
      upload { source remoteRoot }
      status { loggedIn username needsMfa message }
    }
  }
`;

/**
 * Check the stored login, or replace it: username/password are tried against the share first and stored in the
 * endpoint's secret only when the share accepts them, so a mistyped password never replaces a working login.
 */
export const LOGIN_ASPERA = gql`
  mutation LoginAspera($endpointId: ID!, $username: String, $password: String) {
    login(input: { endpointId: $endpointId, username: $username, password: $password }) {
      status
      message
      process { id state message }
    }
  }
`;

/** Shares asks for no MFA today; this is the seam for an endpoint that will (signiant's mailed code). */
export const SUBMIT_ASPERA_MFA = gql`
  mutation SubmitAsperaMfa($endpointId: ID!, $code: String!) {
    submit_mfa(input: { endpointId: $endpointId, code: $code }) {
      status
      message
    }
  }
`;

export const GET_ASPERA_PROCESSES = gql`
  query GetAsperaProcesses($endpointId: ID!, $limit: Int) {
    processes(input: { endpointId: $endpointId, limit: $limit }) {
      id
      kind
      state
      message
      data
      startDate
      endDate
    }
  }
`;

export const BROWSE_ASPERA_SHARE = gql`
  query BrowseAsperaShare($endpointId: ID!, $path: String!) {
    browse(input: { endpointId: $endpointId, path: $path }) {
      name
      path
      type
    }
  }
`;

/** A listing of a direct server, run from its fixed egress address: returns a process to poll with GET_ASPERA_PROCESS. */
export const START_ASPERA_BROWSE = gql`
  mutation StartAsperaBrowse($endpointId: ID!, $path: String!) {
    startBrowse(input: { endpointId: $endpointId, path: $path }) {
      id
      state
    }
  }
`;

export const GET_ASPERA_PROCESS = gql`
  query GetAsperaProcess($processId: ID!) {
    process(input: { processId: $processId }) {
      id
      state
      message
      data
    }
  }
`;

/** A fixed-IP endpoint's worker (Delta DFP); null for any other endpoint. */
export const GET_ASPERA_WORKER_STATUS = gql`
  query GetAsperaWorkerStatus($endpointId: ID!) {
    workerStatus(input: { endpointId: $endpointId }) { state since holdUntil egressIp heldBy message }
  }
`;

/** Warm the worker and hold it 30 minutes; a second call only extends the hold. */
export const START_ASPERA_WORKER = gql`
  mutation StartAsperaWorker($endpointId: ID!) {
    startWorker(input: { endpointId: $endpointId }) { state since holdUntil egressIp heldBy message }
  }
`;
